import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowLeft, Check } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { MagicLinkRequestForm } from '@/components/customer/MagicLinkRequestForm';
import { CustomerPageShell } from '@/components/customer/CustomerPageShell';

/**
 * Where a customer asks for a link to their bookings.
 *
 * This is the landing page for the "manage your bookings" links now carried by
 * every customer transactional email, and the one the public booking page links
 * to, so it is often a customer's first sight of Sloty as something other than
 * one business's booking form. It shares CustomerPageShell with /bookings/:token
 * for that reason: branding here is what distinguishes a real Sloty page from a
 * phishing page reached through an emailed link.
 */
export default function MyBookingsRequestPage() {
  const { t } = useTranslation('myBookings');

  const points = [t('request.point1'), t('request.point2'), t('request.point3')];

  return (
    <CustomerPageShell>
      <Helmet>
        <title>{t('form.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="mx-auto flex max-w-md flex-col gap-5 px-4 py-8 sm:px-6 sm:py-10">
        <div>
          {/*
            This page replaced a dialog, so there is no close button to back out
            of. A business owner who tapped the customer row needs a way back
            that does not rely on the browser's back gesture.
          */}
          <Link
            href="/login"
            className="inline-flex min-h-touch-target items-center gap-1.5 -ml-1 pr-2 text-body-sm font-medium text-soft transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('request.backToSignIn')}
          </Link>

          <h1 className="mt-1 text-h1 text-ink">{t('form.title')}</h1>
          <p className="mt-2 text-body text-soft">{t('form.description')}</p>
        </div>

        <Card>
          <CardContent className="p-5 sm:p-[22px]">
            <MagicLinkRequestForm />
          </CardContent>
        </Card>

        {/* What the portal is for. The form itself already explains how the
            link behaves (form.hint), so this covers the why, not the how. */}
        <ul className="flex flex-col gap-2.5 rounded-2xl border border-brand-200 bg-brand-50 p-4">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-2.5 text-body-sm text-brand-700">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-500" aria-hidden="true" />
              {point}
            </li>
          ))}
        </ul>
      </div>
    </CustomerPageShell>
  );
}
